import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Home, Compass, MessageSquare, Bell, Bookmark, Heart, MessageCircle, Share2,
  MoreHorizontal, Image as ImageIcon, Video, GraduationCap, Feather, Search,
  Plus, X, Loader2, Send, Trash2, ChevronRight, Flag,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { MEDIA } from "@/lib/media";

/* ---------------- helpers ---------------- */
const AV_COLORS = ["#7A4E2D", "#3E5E45", "#8A5A44", "#5C6B61", "#6A4E3A", "#4A5A6B", "#8A6D3B"];
export function Avatar({ name, src, size = 40 }) {
  const label = name || "Hunter";
  const initials = label.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const color = AV_COLORS[label.length % AV_COLORS.length];
  if (src) return <img src={src} alt={label} className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />;
  return (
    <span className="rounded-full grid place-items-center text-white font-semibold shrink-0" style={{ width: size, height: size, background: color, fontSize: size * 0.36 }}>
      {initials}
    </span>
  );
}

export function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

async function compress(file, max = 1400, q = 0.82) {
  const dataUrl = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file); });
  const img = await new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = dataUrl; });
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL("image/jpeg", q);
}

const TABS = [
  { key: "foryou", label: "For You", icon: Compass },
  { key: "following", label: "Following", icon: Home },
  { key: "recent", label: "Recent", icon: MessageSquare },
  { key: "popular", label: "Popular", icon: Heart },
];
const RAIL = [
  { icon: Home, label: "Home", to: "/archive" },
  { icon: Compass, label: "Explore", to: "/explore" },
  { icon: MessageSquare, label: "Messages", to: "/account" },
  { icon: Bell, label: "Notifications", to: "/notifications" },
  { icon: Bookmark, label: "Saved", to: "/account" },
];

/* ---------------- page ---------------- */
export default function Archive() {
  const { isAuthed, user } = useAuth();
  const { openAuth } = useUI();
  const [tab, setTab] = useState("foryou");
  const [posts, setPosts] = useState(null);
  const [overview, setOverview] = useState(null);
  const [query, setQuery] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeKind, setComposeKind] = useState("photo");
  const [unread, setUnread] = useState(0);
  useEffect(() => { if (isAuthed) api.notificationsUnread().then((r) => setUnread(r.unread || 0)).catch(() => {}); }, [isAuthed]);

  const loadFeed = () => {
    setPosts(null);
    const p = tab === "following" && isAuthed ? api.followingFeed() : api.posts();
    p.then(setPosts).catch(() => setPosts([]));
  };
  useEffect(loadFeed, [tab, isAuthed]);
  useEffect(() => { api.archiveOverview().then(setOverview).catch(() => setOverview(null)); }, []);

  const refresh = () => { loadFeed(); api.archiveOverview().then(setOverview).catch(() => {}); };

  const shown = useMemo(() => {
    let list = posts || [];
    if (tab === "popular") list = [...list].sort((a, b) => (b.liked_by?.length || 0) - (a.liked_by?.length || 0));
    if (query.trim()) {
      const ql = query.toLowerCase();
      list = list.filter((p) => (p.text || "").toLowerCase().includes(ql) || (p.author_name || "").toLowerCase().includes(ql) || (p.tags || []).some((t) => t.toLowerCase().includes(ql)));
    }
    return list;
  }, [posts, tab, query]);

  const openCompose = (kind) => { if (!isAuthed) return openAuth("register"); setComposeKind(kind); setComposeOpen(true); };

  return (
    <div className="bg-paper min-h-screen relative overflow-hidden" data-testid="archive-page">
      <SiteHeader />
      <TreeBackdrop />

      {/* Hero */}
      <section className="relative bg-charcoal text-bone overflow-hidden" data-testid="archive-hero">
        <div className="absolute inset-0 z-0">
          <img src={MEDIA.archiveHero} alt="Hunters around a campfire" className="w-full h-full object-cover object-center" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/85 to-charcoal/20" />
        </div>
        <div className="relative z-10 max-w-[1240px] mx-auto px-5 sm:px-10 py-14 lg:py-16">
          <h1 className="font-serif text-5xl sm:text-6xl leading-[0.95] tracking-tight">The Archive</h1>
          <p className="text-bone/80 mt-4 max-w-md leading-relaxed">Stories, lessons, photos, and traditions from hunters across the country.</p>
          <button onClick={() => openCompose("story")} className="mt-6 inline-flex items-center gap-2 bg-bone text-charcoal px-5 py-3 font-semibold text-sm hover:bg-white transition-colors" data-testid="archive-hero-share">
            Share Your Story <Plus size={16} />
          </button>
        </div>
      </section>

      {/* Feed nav */}
      <div className="bg-bone/70 border-b border-saddle/15 sticky top-0 z-30 backdrop-blur">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-10 flex items-center justify-between gap-4">
          <nav className="flex items-center gap-6" data-testid="feed-tabs">
            {TABS.map((t) => {
              const Icon = t.icon; const active = tab === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} className={`relative flex items-center gap-1.5 py-4 text-sm transition-colors ${active ? "text-charcoal font-semibold" : "text-charcoal/55 hover:text-charcoal"}`} data-testid={`feed-tab-${t.key}`}>
                  <Icon size={15} /> {t.label}
                  {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-blaze" />}
                </button>
              );
            })}
          </nav>
          <div className="relative hidden sm:block w-64 max-w-[40vw]">
            <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search posts, people, topics…" className="w-full bg-white border border-saddle/20 rounded-full pl-4 pr-9 py-2 text-sm outline-none focus:border-forest" data-testid="feed-search" />
          </div>
        </div>
      </div>

      {/* 3-column layout */}
      <div className="max-w-[1240px] mx-auto px-3 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[56px_1fr_324px] gap-5 py-6 relative z-10">
        {/* Left rail */}
        <aside className="hidden lg:flex flex-col items-center gap-7 pt-4 border-r border-saddle/15" data-testid="left-rail">
          {RAIL.map((r) => { const Icon = r.icon; return (
            <Link key={r.label} to={r.to} title={r.label} className="relative text-charcoal/60 hover:text-blaze transition-colors" data-testid={`rail-${r.label.toLowerCase()}`}>
              <Icon size={21} strokeWidth={1.6} />
              {r.label === "Notifications" && unread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-blaze text-white text-[10px] font-bold grid place-items-center" data-testid="rail-notif-badge">{unread > 9 ? "9+" : unread}</span>}
            </Link>
          ); })}
        </aside>

        {/* Center feed */}
        <main className="min-w-0 space-y-5" data-testid="feed">
          {/* Composer */}
          <div className="bg-white border border-saddle/20 rounded-md p-4" data-testid="composer">
            <div className="flex items-center gap-3">
              <Avatar name={user?.display_name} size={40} />
              <button onClick={() => openCompose("photo")} className="flex-1 text-left text-charcoal/45 text-sm bg-paper border border-saddle/15 rounded-full px-4 py-2.5 hover:border-saddle/30 transition-colors" data-testid="composer-open">
                Share a story, photo, lesson, or tradition…
              </button>
            </div>
            <div className="flex items-center gap-1 mt-3 pt-3 border-t border-saddle/12 text-charcoal/60">
              <ComposerBtn icon={ImageIcon} label="Photo" onClick={() => openCompose("photo")} testid="composer-photo" />
              <ComposerBtn icon={Video} label="Video" onClick={() => openCompose("video")} testid="composer-video" />
              <ComposerBtn icon={GraduationCap} label="Lesson" onClick={() => openCompose("lesson")} testid="composer-lesson" />
              <ComposerBtn icon={Feather} label="Tradition" onClick={() => openCompose("tradition")} testid="composer-tradition" />
              <span className="ml-auto text-charcoal/40"><MoreHorizontal size={18} /></span>
            </div>
          </div>

          {/* Posts */}
          {posts === null ? (
            <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
          ) : shown.length === 0 ? (
            <div className="bg-white border border-dashed border-saddle/30 rounded-md p-12 text-center" data-testid="feed-empty">
              <p className="font-serif text-2xl text-forest">{tab === "following" ? "Your following feed is quiet." : "No stories yet."}</p>
              <p className="text-charcoal/60 mt-2">{tab === "following" ? "Follow other hunters to see their stories here." : "Be the first to share a hunt, lesson, or tradition with the community."}</p>
              <button onClick={() => openCompose("story")} className="mt-5 inline-flex items-center gap-2 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="feed-empty-share">Share Your Story <Plus size={15} /></button>
            </div>
          ) : (
            shown.map((p) => <PostCard key={p.id} post={p} me={user} isAuthed={isAuthed} openAuth={openAuth} onChange={refresh} />)
          )}
        </main>

        {/* Right sidebar */}
        <aside className="hidden lg:block space-y-4" data-testid="sidebar">
          <TopContributors data={overview?.top_contributors} />
          <TrendingTopics data={overview?.trending_topics} onPick={setQuery} />
          <RecentActivity data={overview?.recent_activity} />
          <ExploreByState data={overview?.state_markers} />
        </aside>
      </div>

      {/* Bottom CTA */}
      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 pb-14 relative z-10">
        <div className="bg-charcoal text-bone rounded-md px-6 sm:px-10 py-6 flex items-center gap-5" data-testid="archive-cta">
          <span className="hidden sm:grid w-12 h-12 rounded-full border-2 border-bone/40 place-items-center shrink-0"><Compass size={24} className="text-bone/80" /></span>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-2xl leading-tight">Every story adds to something bigger.</p>
            <p className="text-bone/65 text-sm mt-1">Share your experiences and inspire other hunters.</p>
          </div>
          <button onClick={() => openCompose("story")} className="inline-flex items-center gap-2 bg-blaze text-white px-5 py-3 rounded-md text-sm font-semibold hover:bg-blaze-light transition-colors shrink-0" data-testid="archive-cta-share">Share Your Story <Plus size={16} /></button>
        </div>
      </div>

      {composeOpen && <CreatePostModal kind={composeKind} onClose={() => setComposeOpen(false)} onDone={() => { setComposeOpen(false); refresh(); }} />}
    </div>
  );
}

const ComposerBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm hover:bg-paper hover:text-forest transition-colors" data-testid={testid}>
    <Icon size={16} /> <span className="hidden sm:inline">{label}</span>
  </button>
);

const Tree = ({ className }) => (
  <svg viewBox="0 0 100 140" className={className} aria-hidden="true"><path fill="#3E5E45" d="M50 4 L70 44 L60 44 L78 84 L64 84 L86 128 L14 128 L36 84 L22 84 L40 44 L30 44 Z M46 128 h8 v12 h-8 Z" /></svg>
);
function TreeBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 opacity-[0.05]" aria-hidden="true">
      <div className="flex items-end justify-between px-2">
        <div className="flex items-end gap-2"><Tree className="w-24 h-40" /><Tree className="w-16 h-28" /><Tree className="w-20 h-32" /></div>
        <div className="flex items-end gap-2"><Tree className="w-20 h-32" /><Tree className="w-16 h-28" /><Tree className="w-24 h-40" /></div>
      </div>
    </div>
  );
}

/* ---------------- post card ---------------- */
export function PostCard({ post, me, isAuthed, openAuth, onChange }) {
  const [p, setP] = useState(post);
  const [showAll, setShowAll] = useState(false);
  const [comment, setComment] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => setP(post), [post]);

  const liked = isAuthed && (p.liked_by || []).includes(me?.id);
  const media = p.media_list?.length ? p.media_list : (p.media ? [p.media] : []);
  const comments = p.comments || [];

  const like = async () => {
    if (!isAuthed) return openAuth("register");
    try { const u = await api.likePost(p.id); setP(u); } catch { /* noop */ }
  };
  const addComment = async () => {
    if (!isAuthed) return openAuth("register");
    const t = comment.trim(); if (!t) return;
    try { const u = await api.commentPost(p.id, t); setP(u); setComment(""); } catch { toast.error("Couldn't post your comment."); }
  };
  const save = async () => {
    if (!isAuthed) return openAuth("register");
    const next = !saved;
    setSaved(next);
    try { await api.toggleSave({ item_type: "post", item_id: p.id }); }
    catch { setSaved(!next); toast.error("Couldn't update your saved posts."); }
  };
  const share = async () => {
    const url = `${window.location.origin}/archive`;
    try { if (navigator.share) { await navigator.share({ title: "HomeRange Archive", url }); return; } } catch { return; }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied."); } catch { /* noop */ }
  };
  const remove = async () => {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try { await api.deletePost(p.id); toast.success("Post deleted"); onChange?.(); } catch { toast.error("Couldn't delete"); }
  };

  const meta = [p.state, timeAgo(p.created_at)].filter(Boolean).join(" • ");

  return (
    <article className="bg-white border border-saddle/20 rounded-md" data-testid={`post-${p.id}`}>
      {/* header */}
      <div className="flex items-start gap-3 p-4">
        <Link to={`/hunter/${p.user_id}`} data-testid={`post-author-${p.id}`}><Avatar name={p.author_name} size={42} /></Link>
        <div className="min-w-0 flex-1">
          <Link to={`/hunter/${p.user_id}`} className="font-semibold text-charcoal leading-tight hover:text-blaze transition-colors">{p.author_name}</Link>
          <p className="text-xs text-charcoal/50">{meta}</p>
        </div>
        <div className="relative">
          <button onClick={() => setMenuOpen((o) => !o)} className="text-charcoal/40 hover:text-charcoal p-1" aria-label="Post menu" data-testid={`post-menu-${p.id}`}><MoreHorizontal size={18} /></button>
          {menuOpen && (
            <div className="absolute right-0 top-8 bg-white border border-saddle/20 rounded shadow-lg z-20 w-40 py-1 text-sm" onMouseLeave={() => setMenuOpen(false)}>
              {me?.id === p.user_id ? (
                <button onClick={remove} className="w-full text-left px-3 py-2 text-[#B04A3A] hover:bg-paper flex items-center gap-2" data-testid={`post-delete-${p.id}`}><Trash2 size={14} /> Delete</button>
              ) : (
                <button onClick={() => { setMenuOpen(false); toast.success("Thanks — our team will review this post."); }} className="w-full text-left px-3 py-2 text-charcoal/70 hover:bg-paper flex items-center gap-2" data-testid={`post-report-${p.id}`}><Flag size={14} /> Report</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* caption */}
      {p.text && <p className="px-4 pb-3 text-charcoal/85 leading-relaxed whitespace-pre-wrap">{p.text}</p>}

      {/* media */}
      {media.length === 1 && p.media_type === "video" ? (
        <video src={media[0]} poster={p.poster} controls className="w-full max-h-[560px] bg-charcoal" />
      ) : media.length === 1 ? (
        <img src={media[0]} alt="" className="w-full max-h-[560px] object-cover" loading="lazy" />
      ) : media.length > 1 ? (
        <div className={`grid gap-1 px-4 ${media.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
          {media.slice(0, 3).map((m, i) => <img key={i} src={m} alt="" className="w-full h-52 object-cover rounded" loading="lazy" />)}
        </div>
      ) : null}

      {/* tags */}
      {(p.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 pt-3">
          {p.tags.map((t) => <span key={t} className="text-xs bg-paper border border-saddle/20 text-charcoal/70 rounded-full px-2.5 py-1" data-testid="post-tag">{t}</span>)}
        </div>
      )}

      {/* engagement */}
      <div className="flex items-center gap-5 px-4 py-3">
        <button onClick={like} className="flex items-center gap-1.5 text-sm text-charcoal/70 hover:text-blaze transition-colors" data-testid={`post-like-${p.id}`}>
          <Heart size={19} className={liked ? "fill-blaze text-blaze" : ""} /> {(p.liked_by || []).length}
        </button>
        <button onClick={() => setShowAll(true)} className="flex items-center gap-1.5 text-sm text-charcoal/70 hover:text-forest transition-colors" data-testid={`post-comment-${p.id}`}>
          <MessageCircle size={19} /> {comments.length}
        </button>
        <button onClick={share} className="text-charcoal/70 hover:text-forest transition-colors" aria-label="Share" data-testid={`post-share-${p.id}`}><Share2 size={19} /></button>
        <button onClick={save} className="ml-auto text-charcoal/70 hover:text-blaze transition-colors" aria-label="Save" data-testid={`post-save-${p.id}`}><Bookmark size={19} className={saved ? "fill-blaze text-blaze" : ""} /></button>
      </div>

      {/* comments */}
      {comments.length > 0 && (
        <div className="border-t border-saddle/12 px-4 py-3 space-y-3">
          {(showAll ? comments : comments.slice(0, 2)).map((c, i) => (
            <Comment key={c.id || i} c={c} idx={i} postId={p.id} me={me} isAuthed={isAuthed} openAuth={openAuth} onPostUpdate={setP} />
          ))}
          {!showAll && comments.length > 2 && (
            <button onClick={() => setShowAll(true)} className="text-sm text-blaze font-semibold hover:underline" data-testid={`post-viewall-${p.id}`}>View all {comments.length} comments</button>
          )}
        </div>
      )}

      {/* add comment */}
      {showAll && (
        <div className="border-t border-saddle/12 px-4 py-3 flex items-center gap-2">
          <Avatar name={me?.display_name} size={30} />
          <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder={isAuthed ? "Add a comment…" : "Sign in to comment"} className="flex-1 bg-paper border border-saddle/15 rounded-full px-3 py-2 text-sm outline-none focus:border-forest" data-testid={`post-comment-input-${p.id}`} />
          <button onClick={addComment} className="text-blaze hover:text-blaze-dark p-1" aria-label="Send comment" data-testid={`post-comment-send-${p.id}`}><Send size={17} /></button>
        </div>
      )}
    </article>
  );
}

/* ---------------- comment (with likes + threaded replies) ---------------- */
function Comment({ c, idx, postId, me, isAuthed, openAuth, onPostUpdate, isReply = false }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const liked = isAuthed && (c.likes || []).includes(me?.id);

  const like = async () => {
    if (!isAuthed) return openAuth("register");
    try { onPostUpdate(await api.likeComment(postId, c.id)); } catch { /* noop */ }
  };
  const sendReply = async () => {
    const t = replyText.trim(); if (!t) return;
    if (!isAuthed) return openAuth("register");
    try { onPostUpdate(await api.replyComment(postId, c.id, t)); setReplyText(""); setReplyOpen(false); }
    catch { toast.error("Couldn't post your reply."); }
  };

  return (
    <div className="flex items-start gap-2.5" data-testid={isReply ? `reply-row-${c.id}` : `post-comment-row-${postId}-${idx}`}>
      <Link to={c.user_id ? `/hunter/${c.user_id}` : "#"}><Avatar name={c.author_name} size={isReply ? 26 : 30} /></Link>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-tight">
          {c.user_id ? <Link to={`/hunter/${c.user_id}`} className="font-semibold text-charcoal hover:text-blaze">{c.author_name}</Link> : <span className="font-semibold text-charcoal">{c.author_name}</span>}
          {" "}<span className="text-charcoal/40 text-xs">{timeAgo(c.at)}</span>
        </p>
        <p className="text-sm text-charcoal/80">{c.text}</p>
        <div className="flex items-center gap-4 mt-0.5">
          {c.id && (
            <button onClick={like} className={`text-xs inline-flex items-center gap-1 transition-colors ${liked ? "text-blaze" : "text-charcoal/45 hover:text-blaze"}`} data-testid={`comment-like-${c.id}`}>
              <Heart size={12} className={liked ? "fill-blaze" : ""} /> {(c.likes || []).length || ""}
            </button>
          )}
          {!isReply && c.id && <button onClick={() => (isAuthed ? setReplyOpen((o) => !o) : openAuth("register"))} className="text-xs text-charcoal/45 hover:text-forest" data-testid={`comment-reply-btn-${c.id}`}>Reply</button>}
        </div>

        {replyOpen && (
          <div className="flex items-center gap-2 mt-2">
            <input value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} autoFocus placeholder="Write a reply…" className="flex-1 bg-paper border border-saddle/15 rounded-full px-3 py-1.5 text-sm outline-none focus:border-forest" data-testid={`comment-reply-input-${c.id}`} />
            <button onClick={sendReply} className="text-blaze p-1" aria-label="Send reply" data-testid={`comment-reply-send-${c.id}`}><Send size={15} /></button>
          </div>
        )}

        {(c.replies || []).length > 0 && (
          <div className="mt-3 space-y-3 pl-3 border-l border-saddle/15">
            {c.replies.map((r) => (
              <ReplyRow key={r.id} r={r} postId={postId} me={me} isAuthed={isAuthed} openAuth={openAuth} onPostUpdate={onPostUpdate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReplyRow({ r, postId, me, isAuthed, openAuth, onPostUpdate }) {
  const liked = isAuthed && (r.likes || []).includes(me?.id);
  const like = async () => {
    if (!isAuthed) return openAuth("register");
    try { onPostUpdate(await api.likeComment(postId, r.id)); } catch { /* noop */ }
  };
  return (
    <div className="flex items-start gap-2.5" data-testid={`reply-row-${r.id}`}>
      <Link to={r.user_id ? `/hunter/${r.user_id}` : "#"}><Avatar name={r.author_name} size={26} /></Link>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-tight">
          {r.user_id ? <Link to={`/hunter/${r.user_id}`} className="font-semibold text-charcoal hover:text-blaze">{r.author_name}</Link> : <span className="font-semibold text-charcoal">{r.author_name}</span>}
          {" "}<span className="text-charcoal/40 text-xs">{timeAgo(r.at)}</span>
        </p>
        <p className="text-sm text-charcoal/80">{r.text}</p>
        <button onClick={like} className={`text-xs inline-flex items-center gap-1 mt-0.5 transition-colors ${liked ? "text-blaze" : "text-charcoal/45 hover:text-blaze"}`} data-testid={`comment-like-${r.id}`}>
          <Heart size={12} className={liked ? "fill-blaze" : ""} /> {(r.likes || []).length || ""}
        </button>
      </div>
    </div>
  );
}

/* ---------------- sidebar cards ---------------- */
const Card = ({ title, action, children, testid }) => (
  <div className="bg-white border border-saddle/20 rounded-md p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-serif text-lg text-charcoal">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const RANK = ["#C9971F", "#9AA0A6", "#B0764A"];
function TopContributors({ data }) {
  const navigate = useNavigate();
  return (
    <Card title="Top Contributors" action={<Link to="/archive" className="text-xs text-blaze hover:underline">See all</Link>} testid="card-contributors">
      {!data ? <Loader2 className="animate-spin text-forest mx-auto my-4" size={18} /> : data.length === 0 ? (
        <p className="text-sm text-charcoal/50">No contributors yet.</p>
      ) : data.map((c, i) => (
        <button key={c.user_id} onClick={() => navigate(`/hunter/${c.user_id}`)} className="w-full flex items-center gap-3 py-2 text-left" data-testid={`contributor-${i}`}>
          <span className="relative shrink-0">
            <Avatar name={c.name} size={34} />
            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full text-white text-[9px] grid place-items-center font-bold" style={{ background: RANK[i] || "#5C6B61" }}>{i + 1}</span>
          </span>
          <span className="flex-1 text-sm font-medium text-charcoal truncate">{c.name}</span>
          <span className="text-xs text-charcoal/50 tabular-nums">{c.posts} posts</span>
        </button>
      ))}
    </Card>
  );
}

function TrendingTopics({ data, onPick }) {
  return (
    <Card title="Trending Topics" action={<span className="text-xs text-blaze">See all</span>} testid="card-trending">
      {!data ? <Loader2 className="animate-spin text-forest mx-auto my-4" size={18} /> : data.length === 0 ? (
        <p className="text-sm text-charcoal/50">No trending topics yet.</p>
      ) : data.map((t) => (
        <button key={t.topic} onClick={() => onPick?.(t.topic)} className="w-full flex items-center gap-2 py-1.5 text-left group" data-testid="trending-row">
          <span className="text-charcoal/35 font-semibold">#</span>
          <span className="flex-1 text-sm text-charcoal group-hover:text-blaze transition-colors truncate">{t.topic}</span>
          <span className="text-xs text-charcoal/45">{t.count} post{t.count === 1 ? "" : "s"}</span>
        </button>
      ))}
    </Card>
  );
}

function RecentActivity({ data }) {
  return (
    <Card title="Recent Activity" testid="card-activity">
      {!data ? <Loader2 className="animate-spin text-forest mx-auto my-4" size={18} /> : data.length === 0 ? (
        <p className="text-sm text-charcoal/50">No activity yet.</p>
      ) : (<>
        {data.map((a, i) => (
          <div key={i} className="flex items-center gap-2.5 py-2" data-testid={`activity-${i}`}>
            <Avatar name={a.name} size={30} />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-charcoal leading-tight"><span className="font-semibold">{a.name}</span> <span className="text-charcoal/60">{a.action}</span></p>
              <p className="text-xs text-charcoal/40">{timeAgo(a.at)}</p>
            </div>
            {a.thumb && <img src={a.thumb} alt="" className="w-9 h-9 rounded object-cover shrink-0" />}
          </div>
        ))}
        <Link to="/archive" className="block text-sm text-blaze font-semibold hover:underline mt-2">View all activity</Link>
      </>)}
    </Card>
  );
}

// Approx marker positions (% left/top) on the US map image.
const STATE_XY = {
  Montana: [24, 22], Wyoming: [30, 34], Colorado: [34, 44], Idaho: [21, 30], Oregon: [12, 26],
  Washington: [14, 15], Utah: [26, 43], Arizona: [24, 56], Nevada: [17, 42], "New Mexico": [33, 57],
  Texas: [43, 66], Kansas: [45, 46], Nebraska: [44, 38], Minnesota: [52, 26], Wisconsin: [58, 28],
  Michigan: [63, 30], Iowa: [52, 39], Missouri: [53, 47], Arkansas: [54, 58], Louisiana: [56, 68],
  Florida: [76, 74], Georgia: [72, 62], Tennessee: [64, 55], Kentucky: [66, 50], Ohio: [68, 40],
  Pennsylvania: [76, 37], "New York": [80, 30], Maine: [88, 20], Virginia: [77, 49], "North Carolina": [76, 55],
  California: [10, 44],
};
function ExploreByState({ data }) {
  const markers = (data || []).filter((s) => STATE_XY[s.state]);
  return (
    <Card title="Explore by State" testid="card-states">
      <div className="relative">
        <img src="https://upload.wikimedia.org/wikipedia/commons/3/32/Blank_US_Map_%28states_only%29.svg" alt="Map of the United States" className="w-full opacity-[0.18] grayscale" loading="lazy" />
        {markers.map((s) => { const [l, t] = STATE_XY[s.state]; return (
          <span key={s.state} title={`${s.state} · ${s.count}`} className="absolute w-2 h-2 rounded-full bg-blaze -translate-x-1/2 -translate-y-1/2" style={{ left: `${l}%`, top: `${t}%` }} data-testid={`state-marker-${s.state}`} />
        ); })}
      </div>
      <Link to="/explore" className="block text-sm text-blaze font-semibold hover:underline mt-3" data-testid="states-viewall">View all states</Link>
    </Card>
  );
}

/* ---------------- create post modal ---------------- */
const KINDS = [
  { key: "photo", label: "Photo", icon: ImageIcon },
  { key: "video", label: "Video", icon: Video },
  { key: "lesson", label: "Lesson", icon: GraduationCap },
  { key: "tradition", label: "Tradition", icon: Feather },
];
function CreatePostModal({ kind, onClose, onDone }) {
  const { user } = useAuth();
  const [k, setK] = useState(kind === "story" ? "photo" : kind);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [poster, setPoster] = useState(null);
  const [tags, setTags] = useState("");
  const [state, setState] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const photoRef = useRef(null);
  const videoRef = useRef(null);

  const pickPhotos = async (e) => {
    const files = [...(e.target.files || [])].slice(0, 4 - photos.length);
    for (const f of files) {
      if (!f.type.startsWith("image/")) continue;
      if (f.size > 25 * 1024 * 1024) { toast.error("Image too large (max 25MB)."); continue; }
      try { const c = await compress(f); setPhotos((p) => [...p, c]); } catch { toast.error("Couldn't read image."); }
    }
    if (photoRef.current) photoRef.current.value = "";
  };
  const pickVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Choose a video file."); return; }
    setBusy(true);
    try { const r = await api.uploadPostVideo(f, setUploadPct); setVideoUrl(r.url || r.media || r.path); toast.success("Video uploaded"); }
    catch { toast.error("Video upload failed."); } finally { setBusy(false); setUploadPct(0); }
  };

  const submit = async () => {
    const body = {
      text: text.trim() || null,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      state: state.trim() || null,
      post_kind: k,
    };
    if (k === "video") {
      if (!videoUrl && !body.text) { toast.error("Add a video or some text."); return; }
      body.media = videoUrl; body.media_type = "video"; body.poster = poster;
    } else {
      if (!photos.length && !body.text) { toast.error("Add a photo or some text."); return; }
      body.media_list = photos; body.media_type = "image";
    }
    setBusy(true);
    try { await api.createPost(body); toast.success("Posted to The Archive"); onDone(); }
    catch { toast.error("Couldn't post. Please try again."); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-charcoal/60 grid place-items-center p-4" onClick={onClose} data-testid="create-post-modal">
      <div className="bg-paper w-full max-w-lg max-h-[92vh] overflow-y-auto hr-scroll rounded-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-saddle/20">
          <h3 className="font-serif text-2xl text-charcoal">Share with The Archive</h3>
          <button onClick={onClose} aria-label="Close" data-testid="create-post-close"><X size={20} className="text-charcoal/50" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            {KINDS.map((kk) => { const Icon = kk.icon; return (
              <button key={kk.key} onClick={() => setK(kk.key)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${k === kk.key ? "bg-forest text-bone border-forest" : "border-saddle/25 text-charcoal/70 hover:border-forest"}`} data-testid={`kind-${kk.key}`}><Icon size={14} /> {kk.label}</button>
            ); })}
          </div>

          <div className="flex items-start gap-3">
            <Avatar name={user?.display_name} size={40} />
            <textarea autoFocus rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={k === "lesson" ? "Share a lesson you learned in the field…" : k === "tradition" ? "Tell us about a hunting tradition…" : "Share a story…"} className="flex-1 bg-white border border-saddle/20 rounded px-3 py-2 text-sm outline-none focus:border-forest resize-none" data-testid="create-post-text" />
          </div>

          {k === "video" ? (
            <div>
              <input ref={videoRef} type="file" accept="video/*" onChange={pickVideo} className="hidden" data-testid="create-post-video-input" />
              {!videoUrl ? (
                <button onClick={() => videoRef.current?.click()} disabled={busy} className="w-full border-2 border-dashed border-saddle/30 rounded py-6 text-charcoal/60 hover:border-blaze hover:text-blaze transition-colors flex flex-col items-center gap-1.5" data-testid="create-post-video-btn">
                  {busy ? <><Loader2 size={22} className="animate-spin" /> {uploadPct}%</> : <><Video size={22} /> Upload a video</>}
                </button>
              ) : <video src={videoUrl} controls className="w-full max-h-64 bg-charcoal rounded" />}
            </div>
          ) : (
            <div>
              <input ref={photoRef} type="file" accept="image/*" multiple onChange={pickPhotos} className="hidden" data-testid="create-post-photo-input" />
              <div className="flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative w-20 h-20 rounded overflow-hidden">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setPhotos((a) => a.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-charcoal/80 text-white rounded-full p-0.5" aria-label="Remove"><X size={11} /></button>
                  </div>
                ))}
                {photos.length < 4 && (
                  <button onClick={() => photoRef.current?.click()} className="w-20 h-20 border-2 border-dashed border-saddle/30 rounded grid place-items-center text-charcoal/50 hover:border-blaze hover:text-blaze transition-colors" data-testid="create-post-add-photo"><Plus size={20} /></button>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Topics (comma separated)" className="bg-white border border-saddle/20 rounded px-3 py-2 text-sm outline-none focus:border-forest" data-testid="create-post-tags" />
            <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State (optional)" className="bg-white border border-saddle/20 rounded px-3 py-2 text-sm outline-none focus:border-forest" data-testid="create-post-state" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-saddle/20">
          <button onClick={onClose} className="text-sm font-semibold text-charcoal/60 hover:text-forest">Cancel</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 bg-blaze text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors disabled:opacity-60" data-testid="create-post-submit">
            {busy && <Loader2 size={15} className="animate-spin" />} Post
          </button>
        </div>
      </div>
    </div>
  );
}
